import React, { useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { usePracticeDashboardTabIndicator } from '../PracticeManagement/PracticeDashboard/usePracticeDashboardTabIndicator';
import { canManageChatAccess } from '../../utils/chatPermissions';
import { ROLES, getRoleId } from '../../utils/roleUtils';
import '../PracticeManagement/GlobalDashboard/GlobalDashboard.css';
import './Settings.css';

export const SETTINGS_TAB_IDS = {
  ACCOUNT: 'account',
  SIDEBAR: 'sidebar',
  FOLLOWUP: 'followup',
  CALENDAR: 'calendar',
  CHAT_ACCESS: 'chat-access',
  EMAIL_LOGS: 'email-logs',
  USER_LOGS: 'user-logs',
  MOBILE_APP: 'mobile-app-access',
  USER_ROLES: 'user-roles',
  PERMISSION: 'permission',
};

export function getSettingsTabFromLocation(pathname, searchParams) {
  if (String(pathname || '').startsWith('/permission')) {
    return SETTINGS_TAB_IDS.PERMISSION;
  }
  const tab = searchParams?.get?.('tab');
  return tab || SETTINGS_TAB_IDS.ACCOUNT;
}

export function useSettingsTabs() {
  const { user } = useSelector((state) => state.auth || {});
  const roleId = getRoleId(user);

  return useMemo(() => {
    const hideCalendar =
      roleId === ROLES.CAREGIVER || roleId === ROLES.SYSTEM_CAREGIVER;
    const hideFollowUp = roleId === ROLES.PATIENT;
    const tabs = [
      { id: SETTINGS_TAB_IDS.ACCOUNT, label: 'Account' },
      { id: SETTINGS_TAB_IDS.SIDEBAR, label: 'Sidebar' },
    ];
    if (!hideFollowUp) {
      tabs.push({ id: SETTINGS_TAB_IDS.FOLLOWUP, label: 'Follow Up' });
    }
    if (!hideCalendar) {
      tabs.push({ id: SETTINGS_TAB_IDS.CALENDAR, label: 'Calendar Permission' });
    }
    if (canManageChatAccess(roleId)) {
      tabs.push({ id: SETTINGS_TAB_IDS.CHAT_ACCESS, label: 'Chat Access' });
    }
    if (roleId === ROLES.SUPER_ADMIN || roleId === ROLES.SYSTEM_ADMIN) {
      tabs.push({ id: SETTINGS_TAB_IDS.EMAIL_LOGS, label: 'Email Logs' });
    }
    if (
      roleId === ROLES.SUPER_ADMIN ||
      roleId === ROLES.SYSTEM_ADMIN ||
      roleId === ROLES.PRACTICE_ADMIN
    ) {
      tabs.push({ id: SETTINGS_TAB_IDS.USER_LOGS, label: 'User Logs' });
    }
    if (roleId === ROLES.SUPER_ADMIN) {
      tabs.push({ id: SETTINGS_TAB_IDS.MOBILE_APP, label: 'Mobile App' });
      tabs.push({ id: SETTINGS_TAB_IDS.USER_ROLES, label: 'User Roles' });
    }
    if (roleId === ROLES.SUPER_ADMIN || roleId === ROLES.SYSTEM_ADMIN) {
      tabs.push({ id: SETTINGS_TAB_IDS.PERMISSION, label: 'Permission' });
    }
    return tabs;
  }, [roleId]);
}

/**
 * Shared Settings chrome: title + practice-dashboard-style pill tabs.
 * Use around account settings content and around /permission/* pages.
 */
const SettingsHub = ({ activeTab: activeTabProp, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tabs = useSettingsTabs();

  const activeTab =
    activeTabProp || getSettingsTabFromLocation(location.pathname, searchParams);

  const resolvedTab = useMemo(() => {
    if (tabs.some((t) => t.id === activeTab)) return activeTab;
    return tabs[0]?.id || SETTINGS_TAB_IDS.ACCOUNT;
  }, [tabs, activeTab]);

  const { tabsWrapRef, tabIndicatorStyle, indicatorReady } =
    usePracticeDashboardTabIndicator(resolvedTab, tabs.length);

  const handleTabChange = useCallback(
    (tabId) => {
      if (tabId === SETTINGS_TAB_IDS.PERMISSION) {
        navigate('/permission/roles');
        return;
      }
      navigate(`/settings?tab=${encodeURIComponent(tabId)}`);
    },
    [navigate]
  );

  return (
    <div className="settings-page-wrapper">
      <div className="settings-page-header">
        <h2 className="dashboard-title settings-page-title">Settings</h2>
      </div>
      <div className="settings-card settings-hub-card">
        <nav className="practice-dashboard-nav settings-hub-nav" aria-label="Settings sections">
          <div className="practice-dashboard-tabs-wrap" ref={tabsWrapRef}>
            <span
              className={`practice-dashboard-tab-indicator${
                indicatorReady ? ' practice-dashboard-tab-indicator--ready' : ''
              }`}
              style={tabIndicatorStyle}
              aria-hidden="true"
            />
            <ul className="practice-dashboard-tabs">
              {tabs.map((tab) => (
                <li
                  key={tab.id}
                  className={`tab-item ${resolvedTab === tab.id ? 'active' : ''}`}
                >
                  <button
                    type="button"
                    className="tab-button"
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>
        <div className="settings-hub-body">{children}</div>
      </div>
    </div>
  );
};

export default SettingsHub;
